import {
  pendingChatImmediateAtom,
  pendingChatMessageAtom,
} from '@/atoms/chatLaunchAtoms';
import {
  activeSidebarChatIdAtom,
  sidebarChatIdAtomFamily,
} from '@/atoms/chatSidebarAtoms';
import type { WorkMode } from '@heidi/agents/contracts';
import { readCachedWorkHomeMode, useWorkHomeModePersistence } from '@heidi/agents/data';
import { useQueryClient } from '@tanstack/react-query';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

/** Null until a composer hydrates or the clinician picks a mode. */
const workComposerModeAtom = atom<WorkMode | null>(null);

/** The session whose side panel holds a just-started Evidence chat. */
const openEvidenceChatSessionIdAtom = atom<string | null>(null);

const keepWorkChrome = (): void => undefined;

/**
 * True while this session's panel holds an Evidence chat that has no id yet
 * (a send still streaming its first reply). Without it the panel would fall
 * back to Work home mid-send. Released once the chat has its id, so a later
 * open lands on Work home again.
 */
export const useIsEvidenceChatOpen = (
  sessionId: string,
  evidenceChatId: string | null | undefined
): boolean => {
  const [openSessionId, setOpenSessionId] = useAtom(openEvidenceChatSessionIdAtom);
  const isOpen = openSessionId === sessionId;
  useEffect(() => {
    if (isOpen && evidenceChatId) setOpenSessionId(null);
  }, [evidenceChatId, isOpen, setOpenSessionId]);
  return isOpen;
};

/** Pass a session id to hold its Evidence chat open, null to release it. */
export const useSetEvidenceChatOpen = (): ((sessionId: string | null) => void) =>
  useSetAtom(openEvidenceChatSessionIdAtom);

/**
 * An Evidence-only send: a fresh Evidence session chat takes the panel over and
 * sends the message at once (no open animation to wait on, and a delay would
 * paint the empty Evidence chat first).
 */
export const useStartEvidenceChat = (sessionId: string): ((message: string) => void) => {
  const setActiveSidebarChatId = useSetAtom(activeSidebarChatIdAtom);
  const setSidebarChatId = useSetAtom(sidebarChatIdAtomFamily(sessionId));
  const setEvidenceChatOpen = useSetEvidenceChatOpen();
  const setPendingChatImmediate = useSetAtom(pendingChatImmediateAtom);
  const setPendingChatMessage = useSetAtom(pendingChatMessageAtom);
  return useCallback(
    (message: string) => {
      setActiveSidebarChatId(null);
      setSidebarChatId(null);
      setEvidenceChatOpen(sessionId);
      setPendingChatImmediate(true);
      setPendingChatMessage(message);
    },
    [
      sessionId,
      setActiveSidebarChatId,
      setEvidenceChatOpen,
      setPendingChatImmediate,
      setPendingChatMessage,
      setSidebarChatId,
    ]
  );
};

/**
 * The Work / Evidence-only mode the in-session side panel follows, without
 * writing it. Evidence only shows the classic Evidence session chat.
 */
export const useWorkComposerMode = (): WorkMode => {
  const queryClient = useQueryClient();
  const mode = useAtomValue(workComposerModeAtom);
  return mode ?? readCachedWorkHomeMode(queryClient) ?? 'work';
};

/** True while the mode came from an app action, not a pick: composers skip saving it. */
const isWorkComposerModeUnsavedAtom = atom(false);

const setWorkComposerModeAtom = atom(
  null,
  (_get, set, next: WorkMode, unsaved: boolean) => {
    set(workComposerModeAtom, next);
    set(isWorkComposerModeUnsavedAtom, unsaved);
  }
);

/** Switch modes from outside a composer (the panel's history picker). */
export const useSetWorkComposerMode = (): ((next: WorkMode) => void) => {
  const setMode = useSetAtom(setWorkComposerModeAtom);
  return useCallback((next: WorkMode) => setMode(next, false), [setMode]);
};

/**
 * Switch modes for this app session only, leaving the saved `work-home_mode`
 * as it is (a CDI nudge forcing Work). The next pick saves again.
 */
export const useSetUnsavedWorkComposerMode = (): ((next: WorkMode) => void) => {
  const setMode = useSetAtom(setWorkComposerModeAtom);
  return useCallback((next: WorkMode) => setMode(next, true), [setMode]);
};

/**
 * Evidence only in session is the same saved choice as Work home's
 * (`work-home_mode`): it hydrates from it, and toggling it here writes it back.
 * Mount in one composer at a time — each instance persists.
 */
export const usePersistedWorkMode = (): [WorkMode, (next: WorkMode) => void] => {
  const mode = useWorkComposerMode();
  const storedMode = useAtomValue(workComposerModeAtom);
  const isUnsaved = useAtomValue(isWorkComposerModeUnsavedAtom);
  const setMode = useSetWorkComposerMode();
  // A pick made this app session wins over the saved value, which may still be
  // in flight when the next composer mounts.
  const storedModeRef = useRef(storedMode);
  storedModeRef.current = storedMode;
  const hydrate = useCallback(
    (next: WorkMode) => {
      if (storedModeRef.current === null) setMode(next);
    },
    [setMode]
  );
  useWorkHomeModePersistence({
    workMode: 'work',
    composerMode: mode,
    setWorkMode: keepWorkChrome,
    setComposerMode: hydrate,
    canPersist: !isUnsaved,
  });
  return [mode, setMode];
};
